/**
 * Defensive browser mapping for the server-owned portal billing envelope.
 * This module validates and copies canonical money fields; it intentionally
 * performs no balance or cross-semester arithmetic.
 */

import { PORTAL_BILLING_LIFECYCLE_VERSION } from './billingLifecycleVersion.js'
import { billingMoneyCents } from './billingMoneyCents.js'
import { wireDateIsValid } from './wireDate.js'

const BILLING_SIGNED_MONEY_FIELDS = new Set(['balanceDue', 'creditBalance'])

function billingFieldsAreValidMoney(summary, fields) {
  return fields.every(field => {
    const cents = billingMoneyCents(summary?.[field])
    return cents !== null && (cents >= 0 || BILLING_SIGNED_MONEY_FIELDS.has(field))
  })
}

function billingSemesterIsValid(semester) {
  return Boolean(
    semester
    && typeof semester.id === 'string'
    && semester.id
    && typeof semester.name === 'string'
    && (typeof semester.start_date === 'string' || semester.start_date === null)
    && (typeof semester.end_date === 'string' || semester.end_date === null)
  )
}

function billingPaymentTotalCents(payments, paymentIds) {
  if (!Array.isArray(payments)) return null
  let totalCents = 0
  for (const payment of payments) {
    const amountCents = billingMoneyCents(payment?.amount)
    if (!(
      typeof payment?.id === 'string'
      && payment.id
      && typeof payment.date === 'string'
      && payment.date
      && typeof payment.note === 'string'
      && amountCents !== null
      && amountCents > 0
    )) return null
    if (paymentIds.has(payment.id)) return null
    paymentIds.add(payment.id)
    totalCents += amountCents
    if (!Number.isSafeInteger(totalCents)) return null
  }
  return totalCents
}

function billingSemesterRefIsValid(value) {
  return Boolean(
    value
    && typeof value.id === 'string'
    && value.id
    && typeof value.name === 'string'
  )
}

function billingWireDateIsValid(value) {
  return wireDateIsValid(value)
}

function billingRolloverData(
  rollovers,
  semesterId,
  rolloverDirectionIds,
  rolloversById,
) {
  if (!Array.isArray(rollovers)) return null
  const totals = { in: 0, out: 0 }
  const normalized = []
  for (const rollover of rollovers) {
    const amountCents = billingMoneyCents(rollover?.amount)
    const source = rollover?.source_semester
    const target = rollover?.target_semester
    const direction = rollover?.direction
    if (!(
      typeof rollover?.id === 'string'
      && rollover.id
      && billingSemesterRefIsValid(source)
      && billingSemesterRefIsValid(target)
      && source.id !== target.id
      && (direction === 'in' || direction === 'out')
      && billingWireDateIsValid(rollover.date)
      && typeof rollover.note === 'string'
      && amountCents !== null
      && amountCents > 0
      && (direction === 'in' ? target.id === semesterId : source.id === semesterId)
    )) return null

    const directionKey = `${rollover.id}\u0000${direction}`
    if (rolloverDirectionIds.has(directionKey)) return null
    rolloverDirectionIds.add(directionKey)
    const fingerprint = JSON.stringify([
      source.id,
      source.name,
      target.id,
      target.name,
      rollover.date,
      amountCents,
      rollover.note,
    ])
    const prior = rolloversById.get(rollover.id)
    if (prior && prior.fingerprint !== fingerprint) return null
    if (prior) {
      prior.directions.add(direction)
    } else {
      rolloversById.set(rollover.id, {
        fingerprint,
        sourceId: source.id,
        targetId: target.id,
        directions: new Set([direction]),
      })
    }
    totals[direction] += amountCents
    if (!Number.isSafeInteger(totals[direction])) return null
    normalized.push({
      id: rollover.id,
      sourceSemester: { id: source.id, name: source.name },
      targetSemester: { id: target.id, name: target.name },
      direction,
      date: rollover.date,
      amount: rollover.amount,
      note: rollover.note,
    })
  }
  return { totals, normalized }
}

function billingObligationsMatch(summary, mode) {
  const familyCents = billingMoneyCents(summary.familyOwes)
  const companyCents = billingMoneyCents(summary.companyOwes)
  if (familyCents === null || companyCents === null) return false
  if (mode === 'credits') {
    return familyCents === 0 && companyCents === 0
  }
  const balanceCents = billingMoneyCents(summary.balanceDue)
  if (balanceCents === null) return false
  if (balanceCents >= 0) {
    return familyCents === balanceCents && companyCents === 0
  }
  return familyCents === 0 && companyCents === -balanceCents
}

function normalizeBillingRow(row, paymentIds, rolloverDirectionIds, rolloversById) {
  const paymentTotalCents = billingPaymentTotalCents(row?.payments, paymentIds)
  if (
    !billingSemesterIsValid(row?.semester)
    || paymentTotalCents === null
    || !Array.isArray(row?.rollovers)
  ) {
    throw new Error('billing_contract_invalid')
  }
  if (row.row_status === 'not_applicable') {
    if (row.summary !== null || row.payments.length !== 0 || row.rollovers.length !== 0) {
      throw new Error('billing_contract_invalid')
    }
    return {
      available: false,
      notApplicable: true,
      semester: row.semester,
      payments: [],
      rollovers: [],
    }
  }
  const summary = row?.summary
  if (summary?.revenueDataStatus === 'inconsistent') {
    const lifecycleError = summary.revenueLifecycleError
    if (
      row.row_status !== 'error'
      || summary.familyOwes !== null
      || summary.companyOwes !== null
      || !lifecycleError
      || typeof lifecycleError.code !== 'string'
      || !lifecycleError.code
      || row.payments.length !== 0
      || row.rollovers.length !== 0
    ) throw new Error('billing_contract_invalid')
    return {
      available: false,
      semester: row.semester,
      lifecycleMode: summary.revenueLifecycleMode || null,
      error: typeof lifecycleError.message === 'string'
        ? lifecycleError.message
        : 'Billing data is unavailable.',
      code: lifecycleError.code,
      payments: [],
      rollovers: [],
    }
  }
  const semesterFields = [
    'semesterCharge', 'netContractValue', 'earnedContractValue',
    'semesterCashReceived', 'semesterCashRefunded', 'discounts',
    'rolloverIn', 'rolloverOut', 'settlementDue', 'balanceDue',
    'familyOwes', 'companyOwes',
  ]
  const mode = summary?.revenueLifecycleMode
  const versionValid = (
    summary?.revenueLifecycleVersion === PORTAL_BILLING_LIFECYCLE_VERSION
  )
    && summary?.revenueDataStatus === 'canonical'
  const modeValid = mode === 'regular'
    ? billingFieldsAreValidMoney(summary, [...semesterFields, 'deferredServiceLiability', 'eligibleUnmadeValue', 'receivableContractValue'])
    : mode === 'trial'
      ? billingFieldsAreValidMoney(summary, semesterFields)
      : mode === 'credits' && billingFieldsAreValidMoney(summary, [
        'creditBalance', 'totalPurchased', 'totalConsumed',
        'semesterConsumed', 'earnedContractValue', 'familyOwes', 'companyOwes',
      ])
  if (
    !versionValid
    || !modeValid
    || row.row_status !== 'applicable'
    || summary.familyOwes < 0
    || summary.companyOwes < 0
    || !billingObligationsMatch(summary, mode)
    || paymentTotalCents !== (
      mode === 'credits' ? 0 : billingMoneyCents(summary.semesterCashReceived)
    )
  ) {
    throw new Error('billing_contract_invalid')
  }
  const semesterMode = mode !== 'credits'
  const rolloverData = billingRolloverData(
    row.rollovers,
    row.semester.id,
    rolloverDirectionIds,
    rolloversById,
  )
  if (!rolloverData) throw new Error('billing_contract_invalid')
  const expectedRolloverInCents = semesterMode ? billingMoneyCents(summary.rolloverIn) : 0
  const expectedRolloverOutCents = semesterMode ? billingMoneyCents(summary.rolloverOut) : 0
  if (
    expectedRolloverInCents === null
    || expectedRolloverOutCents === null
    || rolloverData.totals.in !== expectedRolloverInCents
    || rolloverData.totals.out !== expectedRolloverOutCents
  ) throw new Error('billing_contract_invalid')
  return {
    available: true,
    semester: row.semester,
    lifecycleVersion: summary.revenueLifecycleVersion,
    lifecycleMode: mode,
    grossContract: semesterMode ? summary.semesterCharge : null,
    netContract: semesterMode ? summary.netContractValue : null,
    earnedContract: summary.earnedContractValue,
    deferredServiceLiability: mode === 'regular' ? summary.deferredServiceLiability : null,
    eligibleUnmadeValue: mode === 'regular' ? summary.eligibleUnmadeValue : null,
    receivableContract: mode === 'regular' ? summary.receivableContractValue : null,
    cashReceived: semesterMode ? summary.semesterCashReceived : null,
    cashRefunded: semesterMode ? summary.semesterCashRefunded : null,
    discounts: semesterMode ? summary.discounts : null,
    rolloverIn: semesterMode ? summary.rolloverIn : null,
    rolloverOut: semesterMode ? summary.rolloverOut : null,
    settlementDue: semesterMode ? summary.settlementDue : null,
    signedBalanceDue: semesterMode ? summary.balanceDue : null,
    familyOwes: summary.familyOwes,
    companyOwes: summary.companyOwes,
    creditBalance: mode === 'credits' ? summary.creditBalance : null,
    totalPurchased: mode === 'credits' ? summary.totalPurchased : null,
    totalConsumed: mode === 'credits' ? summary.totalConsumed : null,
    semesterConsumed: mode === 'credits' ? summary.semesterConsumed : null,
    payments: row.payments.map(payment => ({
      id: payment.id,
      date: payment.date,
      amount: payment.amount,
      note: payment.note,
    })),
    rollovers: rolloverData.normalized,
  }
}

export function normalizeBillingPayload(data, expectedSemesterId) {
  if (
    data?.ok !== true
    || typeof data.snapshot_at !== 'string'
    || typeof data.student_id !== 'string'
    || !Array.isArray(data.billing_rows)
  ) throw new Error('billing_contract_invalid')

  const paymentIds = new Set()
  const rolloverDirectionIds = new Set()
  const rolloversById = new Map()
  if (data.billing_status === 'empty') {
    if (
      data.totals !== null
      || data.billing_rows.length > 1
      || (expectedSemesterId !== undefined && data.billing_rows.length !== 1)
      || (expectedSemesterId === undefined && data.billing_rows.length !== 0)
    ) {
      throw new Error('billing_contract_invalid')
    }
    if (data.billing_rows.length === 1) {
      const row = normalizeBillingRow(
        data.billing_rows[0],
        paymentIds,
        rolloverDirectionIds,
        rolloversById,
      )
      if (
        !row.notApplicable
        || (expectedSemesterId !== undefined && row.semester.id !== expectedSemesterId)
      ) throw new Error('billing_contract_invalid')
    }
    return null
  }

  const semesterIds = new Set()
  const rows = data.billing_rows.map(row => {
    const normalized = normalizeBillingRow(
      row,
      paymentIds,
      rolloverDirectionIds,
      rolloversById,
    )
    if (semesterIds.has(normalized.semester.id)) {
      throw new Error('billing_contract_invalid')
    }
    semesterIds.add(normalized.semester.id)
    return normalized
  })
  if (rows.length === 0) throw new Error('billing_contract_invalid')
  // An error (inconsistent) row publishes no rollover directions, so its
  // side of a pairing is unknowable rather than missing (#425).
  const errorSemesterIds = new Set(
    rows.filter(row => row.available === false && row.code).map(row => row.semester.id),
  )
  for (const rollover of rolloversById.values()) {
    if (
      (semesterIds.has(rollover.sourceId) && !errorSemesterIds.has(rollover.sourceId)
        && !rollover.directions.has('out'))
      || (semesterIds.has(rollover.targetId) && !errorSemesterIds.has(rollover.targetId)
        && !rollover.directions.has('in'))
    ) throw new Error('billing_contract_invalid')
  }
  if (
    expectedSemesterId !== undefined
    && (rows.length !== 1 || rows[0].semester.id !== expectedSemesterId)
  ) throw new Error('billing_contract_invalid')

  if (data.billing_status === 'inconsistent') {
    if (
      data.totals !== null
      || !rows.some(row => !row.available && !row.notApplicable)
      || rows.some(row => row.notApplicable)
    ) {
      throw new Error('billing_contract_invalid')
    }
  } else if (
    data.billing_status !== 'canonical'
    || rows.some(row => !row.available)
    || !billingFieldsAreValidMoney(data.totals, ['familyOwes', 'companyOwes'])
    || data.totals.familyOwes < 0
    || data.totals.companyOwes < 0
  ) {
    throw new Error('billing_contract_invalid')
  }

  return {
    snapshotAt: data.snapshot_at,
    studentId: data.student_id,
    billingStatus: data.billing_status,
    totals: data.totals === null ? null : {
      familyOwes: data.totals.familyOwes,
      companyOwes: data.totals.companyOwes,
    },
    rows,
  }
}
